import React, { useRef, useState, useEffect, Fragment } from 'react'
import { Editor, Text, Path, Element, Node } from 'vendor/slate'

import { ReactEditor, useSlateStatic } from '..'
import { useIsomorphicLayoutEffect } from '../hooks/use-isomorphic-layout-effect'
import { validate as uuidValidate } from "uuid";

const lastHash = ['']

/**
 * Leaf content strings.
 */
const String = (props: {
  isLast: boolean
  leaf: Text
  parent: Element
  text: Text
}) => {
  const { isLast, leaf, parent, text } = props
  const editor = useSlateStatic()
  const path = ReactEditor.findPath(editor, text)
  const parentPath = Path.parent(path)
  const { uuid } = leaf
  const [shouldScrollTo, setShouldScrollTo] = useState(false)

  const handleHashHighlight = () => {

    lastHash[0] = ''
    if (uuidValidate(window.location.hash.substring(1))) {
      if (window.location.hash.substring(1) === uuid) {
        setShouldScrollTo(true);
      }
    }
  };

  useEffect(() => {
    window.addEventListener("hashchange", handleHashHighlight, false);

    const isUuidMatch = window.location.hash && uuid === window.location.hash.substring(1) ? true : false;

    if (!window.location.hash) {
      lastHash[0] = ''
    }

    if (isUuidMatch) {
      if (!lastHash.includes(uuid)) {
        setShouldScrollTo(true)
      } else {
        setShouldScrollTo(false)
      }
    }

  }, [handleHashHighlight]);

  useEffect(
    () => () => {
      window.removeEventListener("hashchange", handleHashHighlight, false);
    },
    [handleHashHighlight]
  );

  // COMPAT: Render text inside void nodes with a zero-width space.
  // So the node can contain selection but the text is not visible.
  if (editor.isVoid(parent)) {
    return <ZeroWidthString length={Node.string(parent).length} />
  }

  // COMPAT: If this is the last text node in an empty block, render a zero-
  // width space that will convert into a line break when copying and pasting
  // to support expected plain text.
  if (
    leaf.text === '' &&
    parent.children[parent.children.length - 1] === text &&
    !editor.isInline(parent) &&
    Editor.string(editor, parentPath) === ''
  ) {
    return <ZeroWidthString isLineBreak />
  }

  // COMPAT: If the text is empty, it's because it's on the edge of an inline
  // node, so we render a zero-width space so that the selection can be
  // inserted next to it still.
  if (leaf.text === '') {
    return <ZeroWidthString />
  }

  // COMPAT: Browsers will collapse trailing new lines at the end of blocks,
  // so we need to add an extra trailing new lines to prevent that.
  if (isLast && leaf.text.slice(-1) === '\n') {
    return <TextString uuid={uuid} isTrailing text={leaf.text} shouldScrollTo={shouldScrollTo} />
  }

  return <TextString uuid={uuid} text={leaf.text} shouldScrollTo={shouldScrollTo}  />
}

/**
 * Leaf strings with text in them.
 */
const TextString = (props: { uuid: string, text: string, isTrailing?: boolean, shouldScrollTo: boolean }) => {
  const { uuid, text, isTrailing = false, shouldScrollTo } = props
  const ref = useRef<HTMLSpanElement>(null)

  const getTextContent = () => {
    return `${text ?? ''}${isTrailing ? '\n' : ''}`
  }

  // This is the actual text rendering boundary where we interface with the DOM
  // The text is not rendered as part of the virtual DOM, as since we handle basic character insertions natively,
  // updating the DOM is not a one way dataflow anymore. What we need here is not reconciliation and diffing
  // with previous version of the virtual DOM, but rather diffing with the actual DOM element, and replace the DOM <span> content
  // exactly if and only if its current content does not match our current virtual DOM.
  // Otherwise the DOM TextNode would always be replaced by React as the user types, which interferes with native text features,
  // eg makes native spellcheck opt out from checking the text node.

  // useLayoutEffect: updating our span before browser paint
  useIsomorphicLayoutEffect(() => {
    // null coalescing text to make sure we're not outputing "null" as a string in the extreme case it is nullish at runtime
    const textWithTrailing = getTextContent()

    if (ref.current && ref.current.textContent !== textWithTrailing) {
      ref.current.textContent = textWithTrailing
    }

    // intentionally not specifying dependencies, so that this effect runs on every render
    // as this effectively replaces "specifying the text in the virtual DOM under the <span> below" on each render

    if (ref.current) {
      const element = document.getElementById(uuid)

      if (element) {
        if (shouldScrollTo) {
          lastHash[0] = uuid
          setTimeout(() => {
            ref.current.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" })
            ref.current.animate(
              {
                background: ['yellow', 'inherit']
              },
              5000
            )
          }, 1)
        }
      }
    }
  })


  // Render text content immediately if it's the first-time render
  // Ensure that text content is rendered on server-side rendering
  if (!ref.current) {
    return (
      <span id={uuid} data-slate-string ref={ref} style={{ background: 'inherit' }}>
        {getTextContent()}
      </span>
    )
  }


  // the span is intentionally same on every render in virtual DOM, actual rendering happens in the layout effect above
  return <span id={uuid} data-slate-string ref={ref} style={{ background: 'inherit' }} />

}

/**
 * Leaf strings without text, render as zero-width strings.
 */

const ZeroWidthString = (props: { length?: number; isLineBreak?: boolean }) => {
  const { length = 0, isLineBreak = false } = props
  return (
    <span
      data-slate-zero-width={isLineBreak ? 'n' : 'z'}
      data-slate-length={length}
    >
      {'\uFEFF'}
      {isLineBreak ? <br /> : null}
    </span>
  )
}

export default String
